import { logger } from "@climatepartner/app-shell/monitoring";
import { AxiosError, InternalAxiosRequestConfig } from "axios";
import { v4 } from "uuid";

import { RestError } from "../lib/exceptions";

const REQUEST_ID_HEADER = "x-request-id";

export const sessionExpiredMessage = "Your session has expired. Please log in again.";

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/**
 * A 401 from an auth guard, with or without the CFC envelope. A 401 a service throws with its own
 * message (e.g. a forbidden calculation state change) is a domain error.
 */
export const isAuthenticationFailure = (error: AxiosError): boolean => {
  if (error.response?.status !== 401) {
    return false;
  }
  const data: unknown = error.response.data;
  const body = isRecord(data) && isRecord(data.response) ? data.response : data;
  return !isRecord(body) || typeof body.message !== "string" || body.message === "Unauthorized";
};

/** The x-request-id header enables request tracing across the frontend and backend services. */
export const createRequestIdInterceptor = (excludeRequestId: boolean = false) => {
  return (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if (!excludeRequestId && config.headers) {
      config.headers[REQUEST_ID_HEADER] = v4();
    }
    return config;
  };
};

export const createErrorTransformInterceptor = () => {
  return (error: AxiosError<{ response: RestError }>): Promise<void> => {
    if (isAuthenticationFailure(error)) {
      return Promise.reject(new RestError(sessionExpiredMessage, "Unauthorized", 401));
    }

    if (error.response?.data?.response) {
      const { message, error: responseError, statusCode, ...details } = error.response.data.response;

      logger.error(
        message,
        {
          statusCode: statusCode,
          type: "api-error",
        },
        error
      );

      return Promise.reject(new RestError(message, responseError, statusCode, details));
    }

    return Promise.reject(error);
  };
};
